import Vue from 'vue';
import firebase from 'firebase/app';
import { geocodeLocation } from '@qualle-admin/qutil/dist/geocoder';

const state = {
  empty_locations: [],
  export_locations: [],
  companyLocation: null,
  iana_locations: [],
  vendor_details: {},
};

const mutations = {
  LOAD_EMPTY_LOCATIONS_DATA(state, data) {
    state.empty_locations = data;
  },
  LOAD_EXPORT_LOCATIONS_DATA(state, data) {
    state.export_locations = data;
  },
  set_company_location(state, data) {
    state.companyLocation = data;
  },
  set_iana_locations(state, data) {
    state.iana_locations = data;
  },
  LOAD_VENDOR_DETAILS_DATA(state, data) {
    state.vendor_details = data;
  },
};

const actions = {
  activeLoadData: async ({ commit }, orgId) => {
    try {
      Vue.prototype.$snapshots.$organizations_snapshot = firebase
        .firestore()
        .collection('organizations')
        .doc(orgId)
        .onSnapshot((snapshot) => {
          const {
            address,
            locations,
            empty_locations: emptyLocations = [],
            vendorDetails,
          } = snapshot.data();

          commit('LOAD_EXPORT_LOCATIONS_DATA', locations);
          commit('LOAD_EMPTY_LOCATIONS_DATA', emptyLocations);
          commit('LOAD_VENDOR_DETAILS_DATA', vendorDetails);
          commit('set_company_location', address);
        });
    } catch (error) {
      commit('LOAD_EXPORT_LOCATIONS_DATA', []);
    }
  },
  add_export_location: async ({ dispatch, rootGetters }, payload) => {
    let orgId;
    if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    } else {
      orgId = payload;
    }

    const { address, label } = payload;

    const geocodedLocation = await geocodeLocation(address);

    const preparedExportLocation = {
      label,
      ...geocodedLocation,
      details: {
        primaryContactName: null,
        primaryContact: null,
        secondaryContactName: null,
        secondaryContact: null,
        hoursOfOperation: null,
        pickupInstructions: null,
      },
    };

    if (typeof orgId !== 'undefined') {
      await firebase
        .firestore()
        .collection('organizations')
        .doc(orgId)
        .update({
          locations: firebase.firestore.FieldValue.arrayUnion(
            preparedExportLocation
          ),
        });

      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          message: 'New Export Location Added!',
        },
        { root: true }
      );
    }
  },
  add_empty_location: async ({ dispatch, rootGetters }, payload) => {
    let orgId;
    if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    } else {
      orgId = payload;
    }

    const { address, label } = payload;

    const geocodedLocation = await geocodeLocation(address);

    const preparedEmptyLocation = {
      ...geocodedLocation,
      label,
      details: {
        primaryContactName: null,
        primaryContact: null,
        secondaryContactName: null,
        secondaryContact: null,
        hoursOfOperation: null,
        pickupInstructions: null,
      },
    };

    if (typeof orgId !== 'undefined') {
      await firebase
        .firestore()
        .collection('organizations')
        .doc(orgId)
        .update({
          empty_locations: firebase.firestore.FieldValue.arrayUnion(
            preparedEmptyLocation
          ),
        });

      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          message: 'New Empty Location Added!',
        },
        { root: true }
      );
    }
  },
  add_company_location: async ({ commit, dispatch, rootGetters }, payload) => {
    let orgId;
    if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    } else {
      orgId = payload;
    }

    if (typeof orgId !== 'undefined') {
      await firebase.firestore().collection('organizations').doc(orgId).update({
        address: payload,
      });

      commit('set_company_location', payload);

      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          message: 'Company Location Updated!',
        },
        { root: true }
      );
    }
  },
  update_location: async ({ dispatch, rootGetters }, payload) => {
    let orgId;
    if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    } else {
      orgId = payload;
    }

    const organizationRef = await firebase
      .firestore()
      .collection('organizations')
      .doc(orgId);
    const { empty_locations: emptyLocations } = await (
      await organizationRef.get()
    ).data();

    const existingLocation =
      emptyLocations.findIndex(({ address }) => address === payload.address) >
      -1;

    const locationToUpdate = Object.assign({}, payload);

    if (existingLocation) {
      const updatedLocations = emptyLocations.filter(
        ({ address }) => address !== payload.address
      );

      await organizationRef.update({
        empty_locations: [...updatedLocations, locationToUpdate],
      });
    }

    dispatch(
      'auth/new_notification',
      {
        type: 'notification',
        message: 'Vendor details for empty location updated!',
      },
      { root: true }
    );
  },
  delete_empty_location: async ({ dispatch, rootGetters }, payload) => {
    let orgId;
    if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    } else {
      orgId = payload;
    }

    const organizationRef = await firebase
      .firestore()
      .collection('organizations')
      .doc(orgId);
    const { empty_locations: emptyLocations } = await (
      await organizationRef.get()
    ).data();

    const existingLocation =
      emptyLocations.findIndex(({ address }) => address === payload.address) >
      -1;

    if (existingLocation) {
      const updatedLocations = emptyLocations.filter(
        ({ address }) => address !== payload.address
      );

      await organizationRef.update({
        empty_locations: updatedLocations,
      });
    }

    dispatch(
      'auth/new_notification',
      {
        type: 'notification',
        message: 'Empty Location Deleted!',
      },
      { root: true }
    );
  },
  delete_export_location: async ({ dispatch, rootGetters }, payload) => {
    let orgId;
    if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    } else {
      orgId = payload;
    }

    const organizationRef = await firebase
      .firestore()
      .collection('organizations')
      .doc(orgId);
    const { locations } = await (await organizationRef.get()).data();

    const existingLocation =
      locations.findIndex(({ address }) => address === payload.address) > -1;

    if (existingLocation) {
      const updatedLocations = locations.filter(
        ({ address }) => address !== payload.address
      );

      await organizationRef.update({
        locations: updatedLocations,
      });
    }

    dispatch(
      'auth/new_notification',
      {
        type: 'notification',
        message: 'Export Location Deleted!',
      },
      { root: true }
    );
  },
  add_vendor_details: async ({ dispatch, rootGetters }, payload) => {
    let orgId;

    if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    } else {
      orgId = payload;
    }
    if (typeof orgId !== 'undefined') {
      const organizationRef = await firebase
        .firestore()
        .collection('organizations')
        .doc(orgId);

      await organizationRef.set({ vendorDetails: payload }, { merge: true });

      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          message: 'Vendor Details Updated!',
        },
        { root: true }
      );
    }
  },
  update_iana_locations: async ({ commit }) => {
    const { docs: ianaLocations } = await firebase
      .firestore()
      .collection('iana_locations')
      .get();

    commit(
      'set_iana_locations',
      ianaLocations
        .map((doc) => ({ ...doc.data() }))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  },

  // Updating selected Export location vendor details
  update_Export_location: async ({ dispatch, rootGetters }, payload) => {
    let orgId;
    if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    } else {
      orgId = payload;
    }

    const organizationRef = await firebase
      .firestore()
      .collection('organizations')
      .doc(orgId);
    const { locations: exportLocations } = await (
      await organizationRef.get()
    ).data();

    const existingLocation =
      exportLocations.findIndex(({ address }) => address === payload.address) >
      -1;

    const locationToUpdate = Object.assign({}, payload);

    if (existingLocation) {
      const updatedLocations = exportLocations.filter(
        ({ address }) => address !== payload.address
      );

      await organizationRef.update({
        locations: [...updatedLocations, locationToUpdate],
      });
    }

    dispatch(
      'auth/new_notification',
      {
        type: 'notification',
        message: 'Vendor details for Export location updated!',
      },
      { root: true }
    );
  },
};

const getters = {
  get_iana_locations(state) {
    return state.iana_locations;
  },
  get_export_locations(state) {
    return state.export_locations;
  },
  get_empty_locations(state) {
    return state.empty_locations;
  },
  get_company_location(state) {
    return state.companyLocation;
  },
  get_vendor_details(state) {
    return state.vendor_details;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
